import { useState, useEffect, useCallback } from 'react';
import API from '../services/api';
import {
  Search, RefreshCw, Plus, Trash2, Edit2, X,
  CheckSquare, Square, ExternalLink, Star, Package,
  CheckCircle, AlertTriangle
} from 'lucide-react';

const emitWishlistUpdated = () => {
  window.dispatchEvent(new CustomEvent('wishlist-updated', { detail: { ts: Date.now() } }));
};

// ─── BULK AKTIONSLEISTE ────────────────────────────────────────────────────────
function BulkActionBar({ selectedIds, allItems, onSelectAll, onClearAll, onDelete }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const count = selectedIds.length;
  const allSelected = count > 0 && count === allItems.length;

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    await onDelete(selectedIds);
    setConfirmDelete(false);
  };

  return (
    <div style={{
      background: 'var(--bg2)',
      border: `1px solid ${count > 0 ? 'var(--accent)' : 'var(--border)'}`,
      borderRadius: 10, padding: '10px 16px', marginBottom: 12,
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      transition: 'border-color 0.2s',
    }}>
      <button onClick={allSelected ? onClearAll : onSelectAll} className="btn btn-ghost"
        style={{ padding: '5px 10px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
        {allSelected ? <CheckSquare size={14} color="var(--accent)" /> : <Square size={14} />}
        {allSelected ? 'Alle abwählen' : 'Alle auswählen'}
      </button>

      <span style={{
        fontSize: 12, minWidth: 110, fontFamily: 'Syne', fontWeight: count > 0 ? 700 : 400,
        color: count > 0 ? 'var(--accent)' : 'var(--text3)',
      }}>
        {count > 0 ? `${count} ausgewählt` : 'Nichts ausgewählt'}
      </span>

      <div style={{ width: 1, height: 24, background: 'var(--border)' }} />

      {count > 0 && (
        <>
          <button className="btn" onClick={handleDelete} style={{
            fontSize: 12, padding: '5px 12px',
            background: confirmDelete ? 'var(--danger, #ef4444)' : 'transparent',
            color: confirmDelete ? 'white' : 'var(--danger, #ef4444)',
            border: '1px solid var(--danger, #ef4444)',
            display: 'flex', alignItems: 'center', gap: 5, transition: 'all 0.15s',
          }}>
            <Trash2 size={13} />
            {confirmDelete ? `Wirklich ${count} löschen?` : `Löschen (${count})`}
          </button>
          {confirmDelete && (
            <button className="btn btn-ghost" onClick={() => setConfirmDelete(false)}
              style={{ fontSize: 12, padding: '5px 10px' }}>Abbrechen</button>
          )}
          <button onClick={onClearAll} className="btn btn-ghost"
            style={{ marginLeft: 'auto', padding: '5px 10px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
            <X size={12} /> Auswahl leeren
          </button>
        </>
      )}
    </div>
  );
}

// ─── ADD/EDIT MODAL ────────────────────────────────────────────────────────────
function WishlistModal({ item, onClose, onDone }) {
  const isEdit = !!item;
  const [asin, setAsin] = useState(item?.asin || '');
  const [keywords, setKeywords] = useState(item?.keywords || '');
  const [agentName, setAgentName] = useState(item?.agent_name || '');
  const [agents, setAgents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    API.get('/agents').then(res => setAgents(res.data.agents || [])).catch(() => {});
  }, []);

  const searchUrl = keywords
    ? `https://www.amazon.de/vine/vine-items?search=${encodeURIComponent(keywords)}`
    : null;

  const save = async () => {
    const cleanAsin = asin.trim().toUpperCase();
    const cleanKeywords = keywords.trim();

    if (!cleanAsin && !cleanKeywords) {
      setError('Bitte ASIN oder Keyword/Brand eingeben');
      return;
    }

    if (cleanAsin && !/^[A-Z0-9]{10}$/.test(cleanAsin)) {
      setError('ASIN ungültig (10 Zeichen, z.B. B0GJ61BT5H)');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        item_type: cleanAsin ? 'asin' : 'keyword',
        asin: cleanAsin || null,
        keywords: cleanKeywords || null,
        agent_name: agentName.trim() || null,
        search_url: cleanKeywords
          ? `https://www.amazon.de/vine/vine-items?search=${encodeURIComponent(cleanKeywords)}`
          : null,
      };

      if (isEdit) {
        await API.patch(`/wishlist/${item.id}`, payload);
      } else {
        await API.post('/wishlist', payload);
      }
      emitWishlistUpdated();
      onDone();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const lbl = {
    display: 'block', marginBottom: 6, color: 'var(--text2)',
    fontSize: 11, fontFamily: 'Syne', textTransform: 'uppercase',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
      <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 16, width: 520, padding: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ fontFamily: 'Syne', fontSize: 16, fontWeight: 800 }}>
            {isEdit ? '✏️ Wishlist Eintrag bearbeiten' : '⭐ Zur Wishlist hinzufügen'}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)' }}><X size={20} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={lbl}>ASIN optional</label>
            <input
              value={asin}
              onChange={e => setAsin(e.target.value.toUpperCase())}
              placeholder="B0GJ61BT5H oder leer lassen"
              style={{ fontFamily: 'DM Mono' }}
              disabled={isEdit && item?.item_type === 'asin'}
            />
            {asin.length === 10 && (
              <a href={`https://www.amazon.de/dp/${asin}`} target="_blank" rel="noreferrer"
                style={{ fontSize: 11, color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                <ExternalLink size={10} /> Amazon Produktseite öffnen
              </a>
            )}
          </div>

          <div>
            <label style={lbl}>Agent</label>
            <input
              value={agentName}
              onChange={e => setAgentName(e.target.value)}
              placeholder="Agent Name (optional)"
              list="agents-list"
            />
            <datalist id="agents-list">
              {agents.map(a => <option key={a.agent_id} value={a.display_name} />)}
            </datalist>
          </div>

          <div>
            <label style={lbl}>Keyword / Brand optional</label>
            <input
              value={keywords}
              onChange={e => setKeywords(e.target.value)}
              placeholder="z.B. adidas, nike, Anker Charger..."
            />
            {searchUrl && (
              <a href={searchUrl} target="_blank" rel="noreferrer"
                style={{ fontSize: 11, color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                <ExternalLink size={10} /> Vine Suche öffnen
              </a>
            )}
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
              Mindestens ASIN oder Keyword/Brand eintragen. Keyword-Einträge erzeugen noch keinen Scan-Job, dienen aber als Match-Wunschliste.
            </div>
          </div>

          {error && (
            <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 6 }}>
              <AlertTriangle size={12} /> {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <button className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>
              <CheckCircle size={14} />
              {saving ? 'Speichern...' : isEdit ? 'Speichern' : 'Hinzufügen'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── HAUPTSEITE ────────────────────────────────────────────────────────────────
export default function Wishlist() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const fetchWishlist = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      const res = await API.get('/wishlist', { params });
      setItems(res.data.items);
      setTotal(res.data.total);
      setSelectedIds([]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { fetchWishlist(); }, [fetchWishlist]);

  useEffect(() => {
    const handler = () => fetchWishlist();
    window.addEventListener('wishlist-updated', handler);
    return () => window.removeEventListener('wishlist-updated', handler);
  }, [fetchWishlist]);

  const handleDelete = async (ids) => {
    try {
      await API.post('/wishlist/bulk-delete', { ids });
      fetchWishlist();
    } catch (err) {
      alert('Fehler: ' + (err.response?.data?.error || err.message));
    }
  };

  const toggleRow = (id) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-DE') : '-';

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Wishlist</h1>
          <p style={{ color: 'var(--text2)', marginTop: 4, fontSize: 13 }}>{total} Einträge</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Plus size={14} /> Hinzufügen
          </button>
          <button className="btn btn-ghost" onClick={fetchWishlist}><RefreshCw size={14} /></button>
        </div>
      </div>

      {/* Suche */}
      <div style={{ position: 'relative', marginBottom: 12 }}>
        <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }} />
        <input
          placeholder="Suche nach ASIN, Keyword, Brand, Agent..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ paddingLeft: 36 }}
        />
      </div>

      {/* Bulk Leiste */}
      <BulkActionBar
        selectedIds={selectedIds}
        allItems={items}
        onSelectAll={() => setSelectedIds(items.map(i => i.id))}
        onClearAll={() => setSelectedIds([])}
        onDelete={handleDelete}
      />

      {/* Tabelle */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>Laden...</div>
        ) : items.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <Star size={32} style={{ color: 'var(--text3)', marginBottom: 12 }} />
            <p style={{ color: 'var(--text3)' }}>Keine Einträge gefunden</p>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width: 40, paddingLeft: 14 }}>
                  <input type="checkbox"
                    checked={selectedIds.length === items.length && items.length > 0}
                    onChange={() => selectedIds.length === items.length ? setSelectedIds([]) : setSelectedIds(items.map(i => i.id))}
                    style={{ width: 'auto', cursor: 'pointer' }}
                  />
                </th>
                <th>Datum</th>
                <th>Typ</th>
                <th>ASIN / Keyword</th>
                <th>Agent</th>
                <th title="Gemeinsame Scanner-Treffer der Wishlist">Treffer</th>
                <th title="Bestellungen in deinem persönlichen Datenbereich">Bestellt</th>
                <th>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => {
                const isSelected = selectedIds.includes(item.id);
                return (
                  <tr key={item.id} style={{ background: isSelected ? 'rgba(99,102,241,0.07)' : undefined }}>
                    <td style={{ paddingLeft: 14 }}>
                      <input type="checkbox" checked={isSelected} onChange={() => toggleRow(item.id)}
                        style={{ width: 'auto', cursor: 'pointer' }} />
                    </td>

                    <td style={{ whiteSpace: 'nowrap', color: 'var(--text3)', fontSize: 12 }}>
                      {formatDate(item.created_at)}
                    </td>

                    <td>
                      <span className={item.item_type === 'keyword' ? 'badge badge-purple' : 'badge badge-green'} style={{ fontSize: 11 }}>
                        {item.item_type === 'keyword' || !item.asin ? 'Keyword' : 'ASIN'}
                      </span>
                    </td>

                    <td style={{ maxWidth: 280 }}>
                      {item.asin ? (
                        <a href={`https://www.amazon.de/dp/${item.asin}`} target="_blank" rel="noreferrer"
                          style={{ color: 'var(--accent)', fontFamily: 'DM Mono', fontSize: 12, fontWeight: 600, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                          {item.asin}
                          <ExternalLink size={10} />
                        </a>
                      ) : (
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>
                          {item.keywords || item.brand || '—'}
                        </span>
                      )}

                      {item.asin && item.keywords && (
                        <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text3)' }}>
                          Keyword: {item.keywords}
                        </div>
                      )}

                      {!item.asin && item.search_url && (
                        <a href={item.search_url} target="_blank" rel="noreferrer"
                          style={{ color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: 11, textDecoration: 'none' }}>
                          <ExternalLink size={10} /> Vine Suche öffnen
                        </a>
                      )}
                    </td>

                    <td style={{ fontSize: 12, color: 'var(--text2)' }}>
                      {item.agent_name || <span style={{ color: 'var(--text3)' }}>—</span>}
                    </td>

                    <td>
                      {item.matched_count > 0 ? (
                        <span className="badge badge-green" style={{ fontSize: 11 }}>
                          {item.matched_count}x gefunden
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text3)', fontSize: 12 }}>—</span>
                      )}
                    </td>

                    <td>
                      {parseInt(item.order_count) > 0 ? (
                        <span className="badge badge-purple" style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Package size={10} /> {item.order_count}x
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text3)', fontSize: 12 }}>—</span>
                      )}
                    </td>

                    <td>
                      <button onClick={() => setEditItem(item)} className="btn btn-ghost"
                        style={{ padding: '4px 8px' }} title="Bearbeiten">
                        <Edit2 size={12} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showAdd && (
        <WishlistModal onClose={() => setShowAdd(false)} onDone={fetchWishlist} />
      )}
      {editItem && (
        <WishlistModal item={editItem} onClose={() => setEditItem(null)} onDone={fetchWishlist} />
      )}
    </div>
  );
}